import { spawnSync } from "node:child_process";
// `avatar doctor [--fix]` — Command 07 spec.
// Run a series of health checks and surface ✓/✗ for each. --fix attempts
// auto-fixes for the ones safe to fix automatically.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import boxen from "boxen";
import type { Command } from "commander";
import { checkEnabledToolsHealth } from "../lib/check-enabled-tools-health.js";
import {
  CLAUDE_GITIGNORE_REQUIRED_PATTERNS,
  findMissingClaudeGitignorePatterns,
} from "../lib/claude-gitignore-required-patterns.js";
import { ensureClaudeGitignoreHasRequiredPatterns } from "../lib/ensure-claude-gitignore-has-required-patterns.js";
import { detectSecurityScanners } from "../lib/ensure-security-scanners-installed.js";
import { pathExists } from "../lib/filesystem-helpers.js";
import { findReposWithBlockInGitignore } from "../lib/find-repos-with-block-in-gitignore.js";
import {
  findReposMissingAvatarExclude,
  patchAllReposGitExclude,
} from "../lib/patch-all-repos-git-exclude.js";
import { settingsReferencesBarePython } from "../lib/settings-references-bare-python.js";
import { stripAvatarBlockFromReposGitignore } from "../lib/strip-avatar-block-from-repo-gitignores.js";
import { buildTelemetryDeliveryProbeCheck } from "../lib/telemetry-delivery-probe.js";
import { chalk, log } from "../lib/terminal-logger.js";
import { updateDependentClis } from "../lib/update-dependent-clis.js";
import { isTokenExpired, readUserConfig } from "../lib/user-config-store.js";
import { readReposManifest } from "../lib/workspace-repos-manifest-store.js";

export interface CheckResult {
  name: string;
  status: "ok" | "warn" | "fail";
  detail: string;
  fixable: boolean;
  fix?: () => Promise<void>;
}

export function registerDoctorCommand(program: Command): void {
  program
    .command("doctor")
    .description("Chẩn đoán cài đặt Avatar: hooks, MCP, login, pack, ...")
    .option("--fix", "Tự động fix các issue có thể fix tự động")
    // #250: commander đặt `probe = false` khi gõ `--no-probe`, mặc định `true`.
    // Mặc định BẬT vì đó là toàn bộ lý do check tồn tại — người đang gặp sự cố sẽ
    // không biết mà gõ thêm cờ; cờ tắt dành cho máy offline/CI.
    .option("--no-probe", "Bỏ qua probe POST tới telemetry (máy offline/CI)")
    .action(async (opts: { fix?: boolean; probe?: boolean }) => {
      try {
        const checks = await runChecks(process.cwd(), opts.probe !== false);
        renderChecks(checks);
        if (opts.fix) await applyFixes(checks);
      } catch (err) {
        log.error(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }
    });
}

// Ticket #138 phase-02: dựng check "Repos git exclude (Avatar block)" — tách
// khỏi runChecks để unit-test độc lập (doctor.ts vốn không có test harness).
// Report-only: chỉ set `fix` khi có gì auto-fix được; doctor không --fix thì
// không ghi gì.
//   - manifest rỗng (0 repo) → ok + báo rõ "chưa có repo" (N1: tránh hiểu lầm
//     "đã kiểm tra và pass" khi thật ra không có gì để kiểm tra).
//   - D2/B4a: đo 2 mặt — thiếu block ở info/exclude (`missingExclude`) VÀ còn
//     block cũ sót lại ở .gitignore (`leftoverGitignore`). Repo không kiểm
//     được (không phải git repo / git lỗi) → `uncheckable`, đẩy status sang
//     warn nhưng KHÔNG tính vào `fixable` (N5 — `doctor --fix` không có gì để
//     chạy cho ca này).
export async function buildReposGitExcludeCheck(cwd: string): Promise<CheckResult> {
  const repos = await readReposManifest(cwd);
  if (repos.length === 0) {
    return {
      name: "Repos git exclude (Avatar block)",
      status: "ok",
      detail: "chưa có repo trong src/ (manifest rỗng) — không có gì để kiểm tra",
      fixable: false,
    };
  }

  const { missing: missingExclude, uncheckable } = await findReposMissingAvatarExclude(cwd);
  const leftoverGitignore = await findReposWithBlockInGitignore(cwd);

  // B4a — điều kiện MỚI, KHÔNG giữ gate cũ `missingBlock.length > 0` (gate đó
  // gộp cả 3 trường vào 1 biểu thức ⇒ ca "exclude đã đủ, còn block sót ở
  // .gitignore" sẽ có fix === undefined ⇒ --fix không bao giờ gỡ được).
  const autoFixable = missingExclude.length + leftoverGitignore.length > 0;
  const anyIssue = autoFixable || uncheckable.length > 0;

  const detailParts: string[] = [];
  if (missingExclude.length > 0) detailParts.push(`thiếu exclude ở: ${missingExclude.join(", ")}`);
  if (leftoverGitignore.length > 0)
    detailParts.push(`còn block cũ trong .gitignore ở: ${leftoverGitignore.join(", ")}`);
  if (uncheckable.length > 0)
    detailParts.push(
      `không kiểm được (không phải git repo / git lỗi): ${uncheckable.join(", ")} — xử tay, 'doctor --fix' KHÔNG đụng tới`,
    );

  return {
    name: "Repos git exclude (Avatar block)",
    status: anyIssue ? "warn" : "ok",
    detail: anyIssue
      ? `${detailParts.join("; ")} — chạy 'avatar doctor --fix'`
      : "mọi repo đã ignore Avatar files",
    fixable: autoFixable, // N5: KHÔNG bám `anyIssue` — uncheckable không tự fix được
    fix: autoFixable
      ? async () => {
          // Ticket #138 phase-03 (B4): 2 vế TUẦN TỰ — (1) ghi info/exclude cho
          // repo còn thiếu, (2) gỡ block cũ sót ở .gitignore CHỈ ở repo vừa
          // dựng xong rào mới (`res.patched`) — B1: gỡ trước khi rào mới sẵn
          // sàng = mất ignore vĩnh viễn.
          const res = await patchAllReposGitExclude(cwd);
          // Defense-in-depth: git không dùng được ⇒ không repo nào dựng được rào mới ⇒ DỪNG.
          // Hôm nay `res.patched` đã rỗng sẵn ở nhánh này, nhưng đó là bất biến của HÀM KHÁC —
          // ai "tối ưu" nhánh trả sớm bên đó là `doctor --fix` gỡ .gitignore khi chưa ghi được
          // exclude ⇒ MẤT IGNORE VĨNH VIỄN. Bất biến an toàn phải tự đứng được tại call site.
          if (res.gitOutcome !== "ok") return;
          await stripAvatarBlockFromReposGitignore(cwd, { onlyRepos: res.patched });
        }
      : undefined,
  };
}

// Ticket #172: cổng dò CŨ chỉ tìm chuỗi con `settings.json` trong nội dung file —
// `.claude/.gitignore` của workspace init TRƯỚC #164 CÓ dòng đó nên LUÔN PASS, kéo theo
// fixable=false ⇒ `doctor --fix` không bao giờ chữa được workspace cũ. Cổng mới so đủ danh sách
// pattern bắt buộc, lấy từ CÙNG nguồn sự thật mà `init` và `sync` dùng
// (claude-gitignore-required-patterns.ts).
//
// Tách hàm export vì `runChecks` không export ⇒ check này vốn KHÔNG có test nào. Cùng lý do và
// cùng khuôn với buildReposGitExcludeCheck (#138).
//
// ⛔ `fix` PHẢI dùng đường MERGE (ensureClaudeGitignoreHasRequiredPatterns), KHÔNG dùng writer
//    ghi-đè-toàn-file của đường `init` — nếu không, đúng lúc cổng dò bắt đầu bắt đúng thì `--fix`
//    xoá dòng user. (Tên writer đó cố ý không dán nguyên văn: gate #172 đếm nó trên file này = 0.)
export async function buildClaudeGitignoreCheck(cwd: string): Promise<CheckResult> {
  const name = "🔒 settings.json gitignored (.claude/.gitignore)";
  const path = join(cwd, ".claude", ".gitignore");
  const total = CLAUDE_GITIGNORE_REQUIRED_PATTERNS.length;

  let content: string | null = null;
  try {
    content = await fs.readFile(path, "utf8");
  } catch (err) {
    if ((err as NodeJS.ErrnoException)?.code !== "ENOENT") {
      // Không đọc được (EACCES/EISDIR...) — KHÔNG đề nghị --fix cho thứ ta không đọc nổi.
      return {
        name,
        status: "fail",
        detail: `không đọc được ${path}: ${err instanceof Error ? err.message : String(err)} — xử tay`,
        fixable: false,
      };
    }
  }

  const missing =
    content === null
      ? [...CLAUDE_GITIGNORE_REQUIRED_PATTERNS]
      : findMissingClaudeGitignorePatterns(content);

  if (missing.length === 0) {
    // ⛔ Câu này KHÔNG được hứa "an toàn": cổng chỉ đo TEXT (có mặt đủ dòng pattern), không đo ngữ
    // nghĩa git. Git áp dụng last-match-wins ⇒ một dòng phủ định (`!<tên file>`) đặt sau là file
    // hết bị ignore trong khi đủ cả danh sách. Nói đúng thứ đo được: đếm dòng, không kết luận an toàn.
    return {
      name,
      status: "ok",
      detail: `đủ ${total}/${total} dòng pattern bắt buộc`,
      fixable: false,
    };
  }

  // Ca file VẮNG giữ nguyên câu CRITICAL cũ (không có rào nào cả). Ca thiếu MỘT PHẦN thì liệt kê
  // đúng pattern thiếu — câu "settings.json chứa API key" sẽ là SAI SỰ THẬT khi dòng
  // `settings.json` vẫn còn đó và chỉ thiếu `_backup/`.
  const detail =
    content === null
      ? "CRITICAL: chưa có .claude/.gitignore — settings.json (chứa API key) không được bảo vệ. Chạy 'avatar doctor --fix'"
      : `thiếu ${missing.length}/${total} pattern: ${missing.join(", ")} — chạy 'avatar doctor --fix'`;

  return {
    name,
    status: "fail",
    detail,
    fixable: true,
    fix: async () => {
      const r = await ensureClaudeGitignoreHasRequiredPatterns(cwd);
      // Module fail-soft (không throw) — nhưng applyFixes in "Fixed: ..." vô điều kiện khi fix()
      // resolve. Ném lại ở đây để log không nói dối là đã chữa xong.
      if (r.status === "failed") throw new Error(r.warning ?? "không rõ nguyên nhân");
    },
  };
}

async function runChecks(cwd: string, probe = true): Promise<CheckResult[]> {
  const checks: CheckResult[] = [];

  // 1. Node.js version >= 18.17
  const nodeVer = process.versions.node;
  const [major, minor] = nodeVer.split(".").map((n) => Number.parseInt(n, 10));
  const nodeOk = (major ?? 0) > 18 || ((major ?? 0) === 18 && (minor ?? 0) >= 17);
  checks.push({
    name: "Node.js version",
    status: nodeOk ? "ok" : "fail",
    detail: `v${nodeVer}${nodeOk ? "" : " (cần >= 18.17)"}`,
    fixable: false,
  });

  // 2. Login.
  const config = await readUserConfig();
  if (!config) {
    checks.push({
      name: "Login status",
      status: "fail",
      detail: "Chưa đăng nhập — chạy 'avatar login'",
      fixable: false,
    });
  } else if (isTokenExpired(config)) {
    checks.push({
      name: "Login status",
      status: "warn",
      detail: `Token hết hạn (${config.email}) — chạy 'avatar login'`,
      fixable: false,
    });
  } else {
    checks.push({
      name: "Login status",
      status: "ok",
      detail: `Logged in: ${config.email}`,
      fixable: false,
    });
  }

  // v1.14.0 perf: Batch 1 — 4 probes I/O độc lập chạy parallel qua Promise.all.
  // Save ~80-150ms (4 stat calls × ~30ms mỗi cái → ~30ms total thay vì 120ms).
  // v3: workspace KHÔNG git (pack tarball, repos clone độc lập trong src/).
  // Bỏ check "Git repository" + post-merge hook. Avatar-init = có .claude/pack/ + CLAUDE.md.
  const packPath = join(cwd, ".claude", "pack");
  const claudeMdPath = join(cwd, "CLAUDE.md");
  const [hasPack, hasClaudeMd] = await Promise.all([
    pathExists(packPath),
    pathExists(claudeMdPath),
  ]);

  // 4. .claude/pack/ installation (tarball).
  checks.push({
    name: "team-ai-pack installation",
    status: hasPack ? "ok" : "warn",
    detail: hasPack ? packPath : "Avatar chưa init — chạy 'avatar init'",
    fixable: false,
  });

  // 5. CLAUDE.md present.
  checks.push({
    name: "CLAUDE.md",
    status: hasClaudeMd ? "ok" : "warn",
    detail: hasClaudeMd ? "tồn tại ở project root" : "thiếu — chạy 'avatar init'",
    fixable: false,
  });

  // 5b. Chatops credentials (phase-05, comm-connect) — optional infra cho
  // /avatar:specs:receive. Thiếu → warn (không chặn, không phải mọi dev cần Kanban).
  const mattermostCredPath = join(cwd, ".mattermost", "credentials.json");
  const hasMattermostCreds = await pathExists(mattermostCredPath);
  checks.push({
    name: "Chatops credentials (Mattermost)",
    status: hasMattermostCreds ? "ok" : "warn",
    detail: hasMattermostCreds
      ? ".mattermost/credentials.json tồn tại"
      : "chưa kết nối — chạy 'avatar chatops-connect' để dùng /avatar:specs:receive",
    fixable: false,
  });

  // v3: post-merge hook + workspace .gitignore BỎ (workspace không git).
  // Secrets (API key) sống trong .claude/settings.json, bảo vệ bằng .claude/.gitignore
  // (phòng .claude/ bị git track nhầm). Check file đó có + bảo vệ settings.json.
  // Ticket #172: tách sang buildClaudeGitignoreCheck (test độc lập). Giữ gate hasPack như cũ.
  if (hasPack) {
    checks.push(await buildClaudeGitignoreCheck(cwd));
  }

  // v1.7.0 — 7c. python vs python3 (pack scripts thường hardcode python).
  // Modern macOS chỉ có python3 → pack script fail. Check + suggest alias.
  const pythonCheck = spawnSync("which", ["python"]);
  const python3Check = spawnSync("which", ["python3"]);
  const hasPython = pythonCheck.status === 0;
  const hasPython3 = python3Check.status === 0;
  // Chỉ cảnh báo khi có chỗ THẬT SỰ gọi `python` trần. Trước đây cảnh báo vô điều kiện
  // theo giả định "pack scripts thường ref python" — pack hiện tại toàn `python3`, nên
  // nó chỉ còn là ⚠ vĩnh viễn không chữa được (check này `fixable: false`).
  const barePythonRef = await settingsRefsBarePython(cwd);
  if (hasPython3 && !hasPython && barePythonRef) {
    checks.push({
      name: "Python binary alias",
      status: "warn",
      detail: `Chỉ có python3 nhưng .claude/settings.json có lệnh gọi 'python' trần → sửa: ln -s ${python3Check.stdout.toString().trim()} ~/.local/bin/python`,
      fixable: false,
    });
  } else if (hasPython3 && !hasPython) {
    checks.push({
      name: "Python binary",
      status: "ok",
      detail: `python3: ${python3Check.stdout.toString().trim()} (không lệnh nào ref 'python' trần)`,
      fixable: false,
    });
  } else if (hasPython) {
    checks.push({
      name: "Python binary",
      status: "ok",
      detail: `python: ${pythonCheck.stdout.toString().trim()}`,
      fixable: false,
    });
  } else if (hasPython3) {
    checks.push({
      name: "Python binary",
      status: "ok",
      detail: `python3: ${python3Check.stdout.toString().trim()}`,
      fixable: false,
    });
  }

  // 7c-bis. Ticket #250: probe POST THẬT tới /api/telemetry. Mọi check trên đây đều
  // TĨNH (file có mặt? binary có mặt?) nên mù với ca 2026-08-11 (#208): telemetry tối
  // nhiều ngày mà doctor vẫn xanh hết. Đặt ngay sau check Python vì probe chạy bằng
  // đúng `python3` đó. Tắt bằng `--no-probe` (máy offline / CI).
  if (probe) {
    checks.push(await buildTelemetryDeliveryProbeCheck(cwd));
  } else {
    checks.push({
      name: "Telemetry delivery (probe POST)",
      status: "warn",
      detail: "Bỏ qua theo --no-probe — KHÔNG có kết luận nào về đường gửi telemetry.",
      fixable: false,
    });
  }

  // v1.7.0 — 7d. statusLine path in settings.json — verify file ref tồn tại.
  // Pack template thường ref `node .claude/statusline.cjs` nhưng pack KHÔNG ship file.
  // Avatar v1.7.0 merger đã skip merge nếu file missing, nhưng workspace cũ có thể đã commit settings này.
  const settingsPath = join(cwd, ".claude", "settings.json");
  if (await pathExists(settingsPath)) {
    try {
      const settingsRaw = await fs.readFile(settingsPath, "utf8");
      const settings = JSON.parse(settingsRaw) as {
        statusLine?: { command?: string };
      };
      if (settings.statusLine?.command) {
        const cmd = settings.statusLine.command.trim();
        const match = cmd.match(/^(node|python|python3|bash|sh)\s+([^\s]+)/);
        if (match?.[2]) {
          const refFile = match[2];
          const fullPath = refFile.startsWith("/") ? refFile : join(cwd, refFile);
          const fileExists = await pathExists(fullPath);
          checks.push({
            name: "statusLine command",
            status: fileExists ? "ok" : "fail",
            detail: fileExists
              ? `ref OK: ${refFile}`
              : `BROKEN: settings.json ref '${refFile}' nhưng file không tồn tại. Strip field statusLine hoặc fix path.`,
            fixable: false,
          });
        }
      }
    } catch {
      // settings.json corrupted — đã có check khác handle.
    }
  }

  // 8. Claude Code CLI installed (best-effort `which claude`).
  const which = spawnSync("which", ["claude"]);
  const hasClaudeCli = which.status === 0;
  checks.push({
    name: "Claude Code CLI",
    status: hasClaudeCli ? "ok" : "warn",
    detail: hasClaudeCli ? which.stdout.toString().trim() : "không tìm thấy 'claude' trên PATH",
    fixable: false,
  });

  // 9. Tool toggle health (prompt-scoring, ...). Mỗi tool enabled trong state:
  // manifest còn? hook còn trong settings.json? (skew/orphan → warn; mất hook → fail+fix).
  if (hasPack) {
    const toolChecks = await checkEnabledToolsHealth(cwd);
    checks.push(...toolChecks);
  }

  // 9b. Ticket #138 phase-02: repo trong src/ thiếu Avatar block ở info/exclude,
  // hoặc còn block cũ sót ở .gitignore. Report-only (D2 — đo 2 mặt). Tách
  // helper để test độc lập + xử manifest rỗng (N1). KHÔNG gate hasPack (M1).
  checks.push(await buildReposGitExcludeCheck(cwd));

  // 10. Security scanner cho pull-request-checker (gitleaks secret + trivy CVE).
  // Thiếu → warn (gate tự skip, không fail) + gợi ý cài.
  for (const s of detectSecurityScanners()) {
    checks.push({
      name: `Scanner: ${s.name}`,
      status: s.installed ? "ok" : "warn",
      detail: s.installed
        ? "đã cài"
        : `chưa cài — security gate skip phần này (cài: brew install ${s.name})`,
      fixable: false,
    });
  }

  // 11. CLI phụ thuộc: check version vs latest (autoUpdate=false — chỉ báo).
  //     Cái nào lệch → warn + fixable (doctor --fix sẽ auto-update). gitnexus tự
  //     update ngầm (core tool) nên thường về "ok".
  const cliResults = await updateDependentClis({ autoUpdate: false });
  for (const r of cliResults) {
    const stale = r.action === "update-available";
    const broken = r.action === "binary-broken";
    // #unmanaged: binary không thuộc cây brew (Apple Git...) — `brew upgrade` vô tác
    // dụng nên KHÔNG warn, KHÔNG fixable. Cảnh báo không chữa được chỉ dạy người dùng
    // phớt lờ cảnh báo.
    const unmanaged = r.action === "unmanaged";
    checks.push({
      name: `CLI: ${r.name}`,
      status: stale || broken || r.action === "not-installed" ? "warn" : "ok",
      detail: unmanaged
        ? `v${r.current ?? "?"} — ${r.detail ?? "không do brew quản lý"}`
        : stale
          ? `v${r.current} → v${r.latest} có bản mới`
          : broken
            ? (r.detail ?? "cài dở — binary lỗi")
            : r.action === "not-installed"
              ? "chưa cài"
              : r.action === "skipped-no-manager"
                ? (r.detail ?? "bỏ qua")
                : `v${r.current ?? "?"} (mới nhất)`,
      fixable: stale || broken,
      ...(stale || broken
        ? {
            fix: async () => {
              await updateDependentClis({ autoUpdate: true });
            },
          }
        : {}),
    });
  }

  return checks;
}

function renderChecks(checks: CheckResult[]): void {
  const lines = [chalk.bold("Avatar Doctor"), "─".repeat(48)];
  let passed = 0;
  let issues = 0;
  let fixable = 0;
  for (const c of checks) {
    const icon =
      c.status === "ok"
        ? chalk.green("✓")
        : c.status === "warn"
          ? chalk.yellow("⚠")
          : chalk.red("✗");
    lines.push(`${icon} ${c.name.padEnd(28)} ${chalk.dim(c.detail)}`);
    if (c.status === "ok") passed += 1;
    else {
      issues += 1;
      if (c.fixable) fixable += 1;
    }
  }
  lines.push("─".repeat(48));
  lines.push(
    `${passed} checks passed, ${issues} issue${issues === 1 ? "" : "s"}${fixable > 0 ? ` (${fixable} fixable — chạy 'avatar doctor --fix')` : ""}`,
  );
  process.stdout.write(`${boxen(lines.join("\n"), { padding: 1, borderStyle: "round" })}\n`);
}

/** Đọc `.claude/settings.json` rồi hỏi nó có gọi `python` trần không. Thiếu file ⇒ false. */
async function settingsRefsBarePython(cwd: string): Promise<boolean> {
  try {
    return settingsReferencesBarePython(
      await fs.readFile(join(cwd, ".claude", "settings.json"), "utf8"),
    );
  } catch {
    return false;
  }
}

async function applyFixes(checks: CheckResult[]): Promise<void> {
  let count = 0;
  for (const c of checks) {
    if (c.fixable && c.fix) {
      try {
        await c.fix();
        log.success(`Fixed: ${c.name}`);
        count += 1;
      } catch (err) {
        log.error(`Failed to fix ${c.name}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }
  if (count === 0) log.dim("Không có gì để fix tự động.");
}
